import React, { Component } from 'react';
import { Text, Flex, MultiStepIndicator } from 'monday-ui-react-core';
import { Heading } from "monday-ui-react-core/next";
import classes from "./docs.module.scss";

class Docs extends Component {
  constructor(props) {
    super(props);
    this.state = {
    };
  }

  renderSectionLink = (index, label, anchor) => (
    <Text key={anchor}>
      <a className={classes.link} href={`#${anchor}`}>{`${index}. ${label}`}</a>
    </Text>
  );

  render() {
    return (
      <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.container}>
        <Heading type={Heading.types.H1}>📘 Contents</Heading>
        {[
          ["Overview", "overview"],
          ["Installation", "installation"],
          ["Board setup", "board-setup"],
          ["Drawing flow", "drawing-flow"],
          ["Filters", "filters"],
          ["Saving items to a view", "view-creation"],
          ["Troubleshooting", "troubleshooting"],
          ["Contacts", "contacts"],
          ["Useful links", "links"]
        ].map((section, idx) => this.renderSectionLink(idx + 1, section[0], section[1]))}

        <Heading id="overview" type={Heading.types.H1}>🎲 Overview</Heading>
        <Text className={classes.deviewText}>
          Random Item Picker is a monday.com board view that helps teams select a random sample of items from any board.
          Operators specify the number of items to draw, narrow the pool with dynamic column filters, and save the results to a
          brand-new filtered view so colleagues can review (or action) the selected items later.
        </Text>
        <Text className={classes.deviewText}>
          Popular use cases include:
        </Text>
        <Text className={classes.deviewText}>
          • <strong>Giveaways & contests:</strong> pull random winners from a submissions board to keep draws fair.
        </Text>
        <Text className={classes.deviewText}>
          • <strong>Quality audits:</strong> sample items for QA checks (e.g., pick 25 orders per week to verify).
        </Text>
        <Text className={classes.deviewText}>
          • <strong>Customer interviews:</strong> randomly choose customers or accounts to invite into a research panel.
        </Text>
        <Text className={classes.deviewText}>
          • <strong>Shift or task assignments:</strong> distribute tasks randomly across team members without bias.
        </Text>

        <Heading id="installation" type={Heading.types.H1}>💻 Installation</Heading>
        <Text className={classes.deviewText}>Random Item Picker can be installed by administrators of your monday.com account by going to the following link: <a target="_blank" className={classes.link} href="https://auth.monday.com/oauth2/authorize?client_id=5f2bcf6e7302e4999593f405689382e4&response_type=install">Install</a></Text>

        <Heading id="board-setup" type={Heading.types.H1}>📋 Board setup requirements</Heading>
        <Text className={classes.deviewText}>
          Random Item Picker tags drawn items using a dedicated <strong>Item ID</strong> column so it can
          reproduce the draw inside the created view. When the view opens, the app checks whether such a
          column already exists:
        </Text>
        <Text className={classes.deviewText}>
          • If the column is missing, the view displays a blocking prompt explaining why it is needed and offers a single action—“Create Item ID column”.
        </Text>
        <Text className={classes.deviewText}>
          • Once the column is created, draws can run immediately. The column is named <strong>Random Item Picker Item IDs</strong>.
        </Text>
        <Text className={classes.deviewText}>
          • Only one column of type Item ID is required. If data is deleted later, the view will prompt to recreate it.
        </Text>

        <Heading id="drawing-flow" type={Heading.types.H1}>⚙️ Drawing flow</Heading>
        <Text className={classes.deviewText}>
          1. Select how many items you want to draw (1–1000). The picker automatically caps the number by the board size.
        </Text>
        <Text className={classes.deviewText}>
          2. Optionally pick one or more groups. Filters apply to the entire board by default.
        </Text>
        <Text className={classes.deviewText}>
          3. Configure filters (see next section). Each filter targets one column with an operator/value pair.
        </Text>
        <Text className={classes.deviewText}>
          4. Click <strong>Draw items</strong>. The modal opens instantly with a loading indicator, ensuring users see that the draw is in progress even before items are drawn.
        </Text>
        <Text className={classes.deviewText}>
          5. When the items draw is complete, they are listed in the modal with item IDs and names. You can edit the proposed view name before saving the draw.
        </Text>
        <Text className={classes.deviewText}>
          Under the hood, Random Item Picker uses the classic <strong>Fisher-Yates shuffle</strong> algorithm to randomize items.
          Each permutation of the item list is equally likely, so every eligible item has the same probability of being drawn.
        </Text>

        <Heading id="filters" type={Heading.types.H1}>🔎 Filters</Heading>
        <Text className={classes.deviewText}>
          Filters determine the eligible pool. Supported column types include: Text, Long Text, Status, Dropdown, Tags, Numbers, and Date.
        </Text>
        <Text className={classes.deviewText}>
          • Text-like columns support operators such as “is”, “is not”, “contains”, etc.
        </Text>
        <Text className={classes.deviewText}>
          • Number/Date columns offer comparison operators (“greater than”, “less than or equal”, etc.).
        </Text>

        <Heading id="view-creation" type={Heading.types.H1}>📁 Saving items as a view</Heading>
        <Text className={classes.deviewText}>
          After reviewing the drawn items, click <strong>Save to new view</strong>:
        </Text>
        <Text className={classes.deviewText}>
          • It creates a new board view (table type) with a filter to ensure only the drawn items are displayed.
        </Text>
        <Text className={classes.deviewText}>
          • You can process the drawn items further by navigating to the new view.
        </Text>

        <Heading id="troubleshooting" type={Heading.types.H1}>🛠️ Troubleshooting</Heading>
        <Text className={classes.deviewText}>
          • <strong>No items returned:</strong> loosen filters or confirm groups/columns contain data.
        </Text>

        <Heading id="contacts" type={Heading.types.H1}>☎️ Contacts</Heading>
        <Text className={classes.deviewText}>If you want to get in touch with us, you can use any of the following channels:</Text>
        <Text className={classes.deviewText}>- Through our <a target="_blank" className={classes.link} href="https://deview-studios.atlassian.net/servicedesk/customer/portal/2">service desk</a></Text>
        <Text className={classes.deviewText}>- By email at <a target="_blank" className={classes.link} href="mailto:support@deview-studios.com">support@deview-studios.com</a></Text>
        <Text className={classes.deviewText}>- By submitting a request through the <a target="_blank" className={classes.link} href="https://deview-studios.com/#deview-support">form</a> on our website</Text>
        <Heading id="links" type={Heading.types.H1}>🔗 Useful links</Heading>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/privacy-policy/">Privacy policy</a></Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/terms-of-service/">Terms of service</a></Text>
        <Heading id="apps" type={Heading.types.H1}>🧩 More apps from us</Heading>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/business-card-scanner-for-monday-com/">Business Card Scanner</a> - Easily extract business card data as leads with AI</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/pdf-scanner-for-monday-com/">PDF Scanner</a> - Easily extract PDF details into monday.com boards, using AI</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/ai-image-scanner-for-monday-com/">AI Image Scanner</a> - Easily extract image details to monday.com boards, using AI!</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/passwords-secrets-vault-for-monday-com/">Passwords & Secrets Vault</a> - Securely store secrets and share them with your team</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/personalized-ai-sales-emails-for-monday-com/">Personalized AI Sales Emails</a> - Generate unique personalized sales e-mails, using AI</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/imap-email-connector-for-monday-com/">IMAP Email Connector</a> - Seamlessly connect your IMAP email accounts with monday.com</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/universal-integration-toolkit-for-monday-com/">Universal Integration Toolkit</a> - Easily integrate with any system, without writing code</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/dynamic-column-values-for-monday-com/">Dynamic Column Values</a> - Easy AI-powered automation for monday.com column values</Text>
        <Text className={classes.deviewText}><a target="_blank" className={classes.link} href="https://deview-studios.com/board-doc-backups-for-monday-com/">Board & Doc Backups</a> - Effortlessly backup your boards, groups, items and docs</Text>
      </Flex>
    );
  }
}

export default Docs;
